import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Scoreboard,
  ScoreEntryAdvanced,
  ScoreEntryIntermediate,
  WinnerScreen,
  useBrand,
  usePageTitle,
} from '@aco/shared';
import { ConnectionStatus } from '../components/ConnectionStatus.js';
import { GearMenu } from '../components/GearMenu.js';
import { useGamePage } from '../hooks/useGamePage.js';
import { useAuth } from '../context/AuthContext.js';

/**
 * Main game page. Renders the scoreboard + score entry overlay + gear menu.
 * URL: /game/:gameId
 * URL state: none needed beyond gameId (game state from socket)
 */
export function GamePage() {
  const { gameId } = useParams<{ gameId: string }>();
  const brand = useBrand();
  const { isAuthenticated } = useAuth();

  // ── Anonymous simple mode: local-only scores (no server, no rounds) ──
  // These override the socket-derived scores when active.
  const [anonScores, setAnonScores] = useState<{ team1: number; team2: number } | null>(null);

  const handleScoreZoneTap = useCallback((teamId: 'team1' | 'team2', delta: 1 | -1) => {
    setAnonScores(prev => {
      const base = prev ?? { team1: 0, team2: 0 };
      return { ...base, [teamId]: Math.min(21, Math.max(0, base[teamId] + delta)) };
    });
  }, []);

  const {
    game,
    team1Score,
    team2Score,
    isComplete,
    socketStatus,
    externalUpdate,
    clearExternalUpdate,
    entryTeam,
    gearOpen,
    setGearOpen,
    handleScoreSquareTap,
    handlePprSubmit,
    closeAllOverlays,
    handleToggleScoringMode,
    handleCorrectRound,
    handleNewGame,
    getPprPlayersForEntry,
  } = useGamePage(gameId);

  usePageTitle(
    game
      ? `${game.team1.players[0]?.name} vs ${game.team2.players[0]?.name}`
      : `Game ${gameId}`,
    'Scoreboard',
  );

  if (!game) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100dvh',
          backgroundColor: brand.colors.background,
          color: brand.colors.text,
          fontFamily: 'system-ui, sans-serif',
          fontSize: '1.2rem',
        }}
      >
        Loading game…
      </div>
    );
  }

  if (isComplete) {
    return (
      <>
        <WinnerScreen
          game={game}
          onNewGame={isAuthenticated ? handleNewGame : undefined}
          onGearTap={isAuthenticated ? () => setGearOpen(true) : undefined}
        />
        {isAuthenticated && gearOpen && (
          <GearMenu
            game={game}
            onClose={closeAllOverlays}
            onCloseAll={closeAllOverlays}
            onToggleScoringMode={handleToggleScoringMode}
            onCorrectRound={handleCorrectRound}
          />
        )}
      </>
    );
  }

  // Anonymous simple: use local scores. Authenticated or PPR: use socket scores.
  const isAnonSimple = !isAuthenticated && game.scoringMode === 'simple';
  const displayTeam1Score = isAnonSimple ? (anonScores?.team1 ?? 0) : team1Score;
  const displayTeam2Score = isAnonSimple ? (anonScores?.team2 ?? 0) : team2Score;

  const hasCompletedRounds = game.rounds.length > 0;
  const showGearButton = game.scoringMode !== 'simple' || hasCompletedRounds;
  const anonGameOver = isAnonSimple && (displayTeam1Score === 21 || displayTeam2Score === 21);

  return (
    <div style={{ height: '100dvh', display: 'flex', flexDirection: 'column' }}>
      <ConnectionStatus status={socketStatus} />

      <div style={{ flex: 1, position: 'relative' }}>
        <Scoreboard
          game={game}
          onScoreSquareTap={isAuthenticated ? handleScoreSquareTap : undefined}
          onScoreZoneTap={isAnonSimple ? handleScoreZoneTap : undefined}
          team1ScoreOverride={isAnonSimple ? displayTeam1Score : undefined}
          team2ScoreOverride={isAnonSimple ? displayTeam2Score : undefined}
          flashExternal={externalUpdate}
          onFlashDone={clearExternalUpdate}
        />
      </div>

      {/* Score entry overlays — PPR modes only (authenticated) */}

      {isAuthenticated && entryTeam && game.scoringMode === 'advanced' && (
        <ScoreEntryAdvanced
          players={getPprPlayersForEntry(entryTeam)}
          roundNumber={game.currentRound}
          onSubmit={handlePprSubmit}
          onDismiss={closeAllOverlays}
          onGearTap={showGearButton ? () => setGearOpen(true) : undefined}
        />
      )}

      {isAuthenticated && entryTeam && game.scoringMode === 'intermediate' && (
        <ScoreEntryIntermediate
          players={getPprPlayersForEntry(entryTeam)}
          roundNumber={game.currentRound}
          onSubmit={handlePprSubmit}
          onDismiss={closeAllOverlays}
          onGearTap={showGearButton ? () => setGearOpen(true) : undefined}
        />
      )}

      {/* Anonymous game over — New Game button when either score hits 21 */}
      {anonGameOver && (
        <div style={{ padding: '16px', flexShrink: 0 }}>
          <button
            onClick={handleNewGame}
            style={{
              width: '100%',
              padding: '14px',
              fontSize: '1rem',
              fontWeight: 600,
              border: 'none',
              borderRadius: '10px',
              background: brand.colors.primary,
              color: brand.colors.text,
              fontFamily: 'system-ui, sans-serif',
              cursor: 'pointer',
              touchAction: 'manipulation',
            }}
          >
            New Game
          </button>
        </div>
      )}

      {/* Gear menu — rendered outside scoreboard div so it layers correctly */}
      {isAuthenticated && gearOpen && (
        <GearMenu
          game={game}
          onClose={closeAllOverlays}
          onCloseAll={closeAllOverlays}
          onToggleScoringMode={handleToggleScoringMode}
          onCorrectRound={handleCorrectRound}
        />
      )}
    </div>
  );
}
